/**
 * SwitchPortsSection — the switch's ports, inline on the device sheet.
 *
 * WHY THIS EXISTS
 * Selecting a switch used to show generic AP actions and no ports at all. A "Configure ports"
 * link did exist, but only when `owgw_available === false` -- so onboarding the ECS2100 to
 * uCentral made it disappear, and the operator's reaction was exactly right: "I cannot
 * configure port here, name the ports etc".
 *
 * WHY A FACEPLATE RATHER THAN A TABLE
 * The sheet is ~370px wide. A ten-column port table does not fit, and squeezing one in would
 * mean horizontal scrolling inside a panel -- the thing the DataGrid reflow contract exists to
 * prevent. But a switch's ports are not an abstract list: they are objects in a row on a
 * physical box, and that is how anyone standing at the rack thinks about them. So the port
 * GRID is the control. It shows every port's link state, PoE and uplink in one glance at a
 * fraction of the width, and clicking one opens its detail underneath.
 *
 * ENCODING (consistent with StatusPill's rule: filled = we have it, hollow = we do not)
 *   filled healthy   link up
 *   hollow muted     link down (cable out, or nothing plugged in)
 *   dashed + dimmed  administratively disabled -- a human turned this off, not a fault
 *   bolt             delivering PoE
 *   arrow            the uplink (derived, and load-bearing -- see the PoE guard below)
 *   brand halo       selected. Brand, never the status ramp: a selected Online port and a
 *                    healthy port must not be the same visual statement. The halo is offset
 *                    off the chip's own border so the two do not merge into one thick edge on
 *                    a dense faceplate, and a multi-selection adds a tick — colour is exactly
 *                    the channel a colour-blind operator loses.
 *
 * SELECTING MORE THAN ONE -> THE PORT TABLE (owner, 2026-09-14)
 * Plain click still selects one port and opens its detail: that is the common case and it
 * stays the cheapest gesture. Ctrl/⌘-click adds, shift-click takes a range, and the quick
 * selectors pick by predicate. But the owner compared this faceplate with Meraki's port table
 * and said "it is not easy to select multiple ports and name them and or edit them": a 370px
 * sheet is the wrong place for a many-port edit. So two or more selected ports hand off to the
 * site-wide port table (routes/SwitchPorts) with this switch filtered and the same ports
 * preselected -- "Edit N ports in table →" -- where one Edit dialog names them by pattern and
 * sets VLAN / PoE / edge port / 802.1X with a per-switch preview. The old in-sheet
 * BulkPortPanel is gone: it was a second, weaker bulk editor with its own naming rules.
 *
 * WRITES THAT TOUCH THE DEVICE ARE PROPOSE-THEN-CONFIRM
 * This runs against a live switch that carries the lab, so anything that reaches the hardware
 * renders the exact ecCLI first -- dry_run, no device contact -- and needs a second explicit
 * confirm. Nothing here touches hardware on a first click.
 *
 * NAMES AUTO-SYNC (owner decision 2026-09-14, option C "Meraki auto-sync"). Meridian is the source
 * of truth for a port's name: "Save name" stores it at once and the controller writes it to the
 * switch description in the background, so a rename has no preview. The name shows with its sync
 * badge (Synced / Syncing / Waiting for switch / Failed / Not written to the switch yet) for every
 * role; only msp_admin edits it. Names Meridian does not write on its own -- imported before
 * auto-sync, or on a switch with no site -- get a callout with "Write names to switch".
 * Everything else that reaches the hardware stays propose-then-confirm (above).
 *
 * CURRENT VLANS (owner, 2026-10-07): the detail shows the port's mode, untagged VLAN, tagged VLANs
 * (with site names), live spanning tree, where each fact came from and drift -- from
 * GET switch/port-vlans (services/switch_port_vlans). The VLAN edit starts from those VLANs and
 * sends only the delta; the old single "Access VLAN" box overwrote a trunk blindly. Since OP#1819
 * the tagged list can come from the switch itself ("Read VLANs from switch", one read-only CLI
 * session), and the header line says which source the panel is showing.
 *
 * WHERE THE PORTS COME FROM
 * The uCentral state document first, SNMP only as a fallback. SNMP needs the controller to
 * reach INBOUND to the switch, so an SNMP-backed port view works in the lab and nowhere else;
 * the device's own outbound control channel keeps working behind a customer's NAT. The header
 * shows which transport produced what you are looking at.
 */
import { formatPortRefs, formatSwitchPort, isUcentralPortName } from "@/lib/switchPorts";
import { useMemo, useState } from "react";
import {
  useSwitchDetail,
  usePortVlans,
  usePortConfigApply,
  usePortPoeAction,
  useSetPortLabel,
  type SwitchPortDetail,
  type PortChange,
  type PortConfigResponse,
} from "@/lib/api/hooks";
import { describeApplyOutcome, needsSave } from "@/lib/switchApplyResult";
import { portTableHref, sanitizePortName } from "@/lib/portTable";
import { useSession } from "@/stores/session";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { ErrorBanner } from "@/components/ui/Page";
import { RoleGate } from "@/components/auth/RoleGate";
import {
  AccessPathLine,
  OutcomeNote,
  SaveRunningConfig,
  SaveToStartupCheckbox,
  SwitchSettingsDialog,
} from "@/components/site/SwitchSettingsDialog";
import { NameSyncBadge, NamesNotWrittenCallout } from "@/components/site/NameSyncBadge";
import { cn } from "@/lib/cn";
import {
  buildVlanChange,
  edgeInitial,
  editFromView,
  isTrunkLike,
  vlanDelta,
  vlanSummary,
  type PortVlanView,
  type PortVlansResponse,
  type VlanEdit,
} from "@/lib/portVlans";
import { PortVlanEditor, PortVlanFacts, SwitchVlanReadBar, VlanDiffList } from "@/components/site/PortVlanPanel";

export function SwitchPortsSection({
  serial,
  uplinkPort,
}: {
  serial: string;
  /** The device's known uplink, used to guard PoE actions that could strand the switch. */
  uplinkPort?: string | null;
}) {
  const { data, isLoading, error } = useSwitchDetail(serial);
  /** Current VLANs per port (mode / untagged / tagged / STP / drift). Independent of the port
   *  list: if it fails, the faceplate and the rest of the detail still work. */
  const vlans = usePortVlans(serial);
  const vlanPorts = vlans.data?.ports ?? {};
  /**
   * Selection is a SET, because "name these eight ports AP-1..AP-8" was eight separate
   * select→type→preview→confirm cycles and the operator said so plainly: "very difficult to
   * select multiple ports to edit them at the same time".
   *
   * Single-select stays the default gesture and the cheap path -- a plain click still
   * replaces the selection and opens one port's detail, exactly as before. Multi-select is
   * reached deliberately (ctrl/cmd-click, shift-range, or a quick selector), so the common
   * case never pays for the escape hatch.
   */
  const [selected, setSelected] = useState<string[]>([]);
  /** Anchor for shift-range. Null until a plain click establishes one. */
  const [anchor, setAnchor] = useState<string | null>(null);
  /** Switch-wide settings dialog (802.1X on/off, save running-config). */
  const [settingsOpen, setSettingsOpen] = useState(false);

  const ports = useMemo(
    () => [...(data?.ports ?? [])].sort((a, b) => a.ifindex - b.ifindex),
    [data?.ports],
  );
  const port = selected.length === 1 ? (ports.find((p) => p.name === selected[0]) ?? null) : null;
  const selectedPorts = useMemo(
    () => ports.filter((p) => selected.includes(p.name)),
    [ports, selected],
  );

  // Deep link into the site-wide port table: this switch filtered, the faceplate selection
  // preselected (`?switch=<serial>&ports=3,4,5`, 1-based). Scoped like the nav, so a site sheet
  // lands on the site's table. Non-numeric port names (SNMP spellings) cannot be expressed in
  // `ports` and are simply not preselected.
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  const selectedNumbers = selectedPorts.map((p) => formatSwitchPort(p.name)).filter((n) => /^\d+$/.test(n));
  const tableHref = portTableHref({ orgId, siteId, serial, portNumbers: selectedNumbers });

  /** Click semantics: plain = replace, ctrl/cmd = toggle, shift = range from the anchor. */
  const onChipSelect = (name: string, mods: { toggle: boolean; range: boolean }) => {
    if (mods.range && anchor) {
      const a = ports.findIndex((p) => p.name === anchor);
      const b = ports.findIndex((p) => p.name === name);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        setSelected(ports.slice(lo, hi + 1).map((p) => p.name));
        return;
      }
    }
    if (mods.toggle) {
      setAnchor(name);
      setSelected((prev) =>
        prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
      );
      return;
    }
    setAnchor(name);
    setSelected((prev) => (prev.length === 1 && prev[0] === name ? [] : [name]));
  };

  if (isLoading) return <div className="text-sm text-ink-muted">Reading ports…</div>;

  /**
   * Entry to the switch-WIDE settings (802.1X on/off, save running-config). msp_admin only --
   * the backend gate is is_admin -- so lower roles do not see a control that would 403.
   * Rendered even when ports cannot be read: switch-wide 802.1X is exactly the setting that can
   * make neighbours vanish, and the way back must not depend on the port view working.
   */
  const settingsControl = (
    <RoleGate min="msp_admin" mode="hide">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] text-ink-subtle">802.1X and saving apply to the whole switch</span>
        <Button variant="secondary" onClick={() => setSettingsOpen(true)}>
          Switch settings
        </Button>
      </div>
      {settingsOpen && (
        <SwitchSettingsDialog serial={serial} onClose={() => setSettingsOpen(false)} />
      )}
    </RoleGate>
  );

  // No port list from ANY transport. That is a statement about our visibility, not about the
  // switch -- rendering an empty list would say "this switch has no ports", which is a
  // different and false claim. Same rule as the connectivity sparkline's "no data" hatch.
  if (error || (data && !data.snmp_ok)) {
    return (
      <div className="flex flex-col gap-3">
        <div className="rounded-md border border-line bg-surface-2 p-3">
          <div className="text-sm font-medium text-ink">Ports cannot be read</div>
          <p className="mt-1 text-xs text-ink-muted">
            This switch has not reported its ports over its uCentral connection, and it is not
            reachable by SNMP from the controller either. It does not mean the switch is down —
            only that neither path is currently giving us port state.
          </p>
        </div>
        {/* Why the VLANs are unavailable too (e.g. a SonicFi RSW210G: no state report, no CLI). */}
        <SwitchVlanReadBar serial={serial} readSummary={vlans.data?.device_read} loading={vlans.isLoading} />
        {settingsControl}
      </div>
    );
  }
  if (ports.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <div className="text-sm text-ink-muted">This switch reported no ports.</div>
        {settingsControl}
      </div>
    );
  }

  const s = data?.summary;

  return (
    <div className="flex flex-col gap-3">
      {settingsControl}
      {s && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
          <span className="tabular-nums">{s.total} ports</span>
          <span className="tabular-nums text-healthy">{s.up} up</span>
          <span className="tabular-nums">{s.down} down</span>
          {s.poe > 0 && <span className="tabular-nums">{s.poe} PoE</span>}
          {/* Where this came from. Worth surfacing because the two transports have very
              different reach: uCentral is the device's own outbound channel and keeps working
              behind a customer's NAT, while SNMP needs the controller to route INBOUND to the
              switch and therefore only ever works on a flat network like the lab. An operator
              seeing "via SNMP" is looking at something that will stop working the day this
              switch moves to a site. */}
          {data?.source && (
            <span
              className="text-ink-subtle"
              title={
                data.source === "ucentral"
                  ? "Read over the device's own uCentral connection — works from any site."
                  : data.source === "snmp"
                    ? "Read over SNMP from the controller. This only works while the switch is directly reachable; it will not work behind a site gateway."
                    : "Relayed by a Site Connector."
              }
            >
              via {data.source === "ucentral" ? "uCentral" : data.source}
            </span>
          )}
        </div>
      )}

      {/* Where the port VLANs come from (read from the switch / untagged only / unavailable) and
          "Read VLANs from switch" (OP#1819). */}
      <SwitchVlanReadBar serial={serial} readSummary={vlans.data?.device_read} loading={vlans.isLoading} />

      {/* Names Meridian will not write on its own (imported before auto-sync, or the switch has no
          site), with the explicit "Write names to switch". Nothing renders when there are none. */}
      <NamesNotWrittenCallout serial={serial} ports={ports} />

      {/* The faceplate. Wraps at the sheet's width and stays legible on a 54-port switch. */}
      <div className="flex flex-wrap gap-1">
        {ports.map((p) => (
          <PortChip
            key={p.name}
            port={p}
            isUplink={!!uplinkPort && p.name === uplinkPort}
            vlan={vlanPorts[p.name] ?? null}
            selected={selected.includes(p.name)}
            multi={selected.length > 1}
            onSelect={(mods) => onChipSelect(p.name, mods)}
          />
        ))}
      </div>

      <Legend />

      {/* Quick selectors. On a 54-port ECS5550 "ctrl-click 48 times" is the same problem the
          operator reported, just moved -- so the predicates that actually describe a job
          (every PoE port, every dead port) are one click. */}
      <SelectionBar
        ports={ports}
        selected={selected}
        setSelected={setSelected}
        uplinkPort={uplinkPort}
      />

      {/* Multi-port editing lives in the table. Always visible (discoverability is the
          complaint being answered); prominent once 2+ ports are picked here. */}
      <TableHandoff href={tableHref} selectedNumbers={selectedNumbers} />

      {port && (
        <PortDetail
          // Keyed by port: without it, picking another port kept the previous port's form state.
          key={port.name}
          serial={serial}
          port={port}
          isUplink={!!uplinkPort && port.name === uplinkPort}
          vlanView={vlanPorts[port.name] ?? null}
          vlans={vlans.data}
          vlansLoading={vlans.isLoading}
          vlansError={!!vlans.error || !!vlans.data?.error}
          tableHref={tableHref}
          onClose={() => setSelected([])}
        />
      )}
    </div>
  );
}

/**
 * Select-by-predicate, plus the count and a way out.
 *
 * Deliberately renders even with nothing selected: discoverability is the whole point. An
 * operator who does not know multi-select exists will keep doing it one port at a time, which
 * is the complaint this is answering.
 */
function SelectionBar({
  ports,
  selected,
  setSelected,
  uplinkPort,
}: {
  ports: SwitchPortDetail[];
  selected: string[];
  setSelected: (next: string[]) => void;
  uplinkPort?: string | null;
}) {
  const pick = (fn: (p: SwitchPortDetail) => boolean) =>
    setSelected(ports.filter(fn).map((p) => p.name));

  // "Access ports" = everything except the uplink. It is the selection an operator actually
  // wants when naming or VLAN-ing a switch, and building it by hand is what they complained
  // about.
  const access = (p: SwitchPortDetail) => !uplinkPort || p.name !== uplinkPort;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
      <span className="text-ink-subtle">Select</span>
      <SelectLink onClick={() => pick(access)}>access ports</SelectLink>
      <SelectLink onClick={() => pick((p) => p.status === "up")}>up</SelectLink>
      <SelectLink onClick={() => pick((p) => p.status !== "up")}>down</SelectLink>
      <SelectLink onClick={() => pick((p) => !!p.poe_enabled)}>PoE</SelectLink>
      <SelectLink onClick={() => pick(() => true)}>all</SelectLink>
      {selected.length > 0 && (
        <>
          <span className="text-ink-subtle">·</span>
          <span className="tabular-nums text-ink-muted">{selected.length} selected</span>
          <SelectLink onClick={() => setSelected([])}>clear</SelectLink>
        </>
      )}
      <span className="w-full text-ink-subtle">
        Click a port, or ctrl/⌘-click to add · shift-click for a range
      </span>
    </div>
  );
}

function SelectLink({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded px-1 font-medium text-brand hover:underline"
    >
      {children}
    </button>
  );
}

/** One port on the faceplate. */
function PortChip({
  port,
  isUplink,
  vlan,
  selected,
  multi,
  onSelect,
}: {
  port: SwitchPortDetail;
  isUplink: boolean;
  /** Current VLANs (trunk marker + tooltip); null while unknown. */
  vlan: PortVlanView | null;
  selected: boolean;
  /** True once more than one port is selected — the chip then shows an explicit tick. */
  multi: boolean;
  onSelect: (mods: { toggle: boolean; range: boolean }) => void;
}) {
  const up = port.status === "up";
  // admin_up === false is a deliberate human act, so it reads differently from a dead link:
  // dashed border says "switched off", not "broken". Strict === false on purpose: the
  // uCentral path reports null (the state document cannot tell admin-down from link-down),
  // and a null must not be rendered as "someone disabled this port".
  const adminDown = port.admin_up === false;
  const label = port.alias || port.descr || port.name;

  return (
    <button
      type="button"
      onClick={(e) => onSelect({ toggle: e.ctrlKey || e.metaKey, range: e.shiftKey })}
      onKeyDown={(e) => {
        // Keyboard parity: space/enter carry the same modifiers, so a keyboard user can build
        // the same selection a mouse user can. Without this, multi-select is mouse-only.
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onSelect({ toggle: e.ctrlKey || e.metaKey, range: e.shiftKey });
        }
      }}
      aria-pressed={selected}
      title={`Port ${formatSwitchPort(port.name)}${label && label !== port.name ? ` — ${formatPortRefs(label)}` : ""}\n${
        adminDown ? "Administratively disabled" : up ? "Link up" : "Link down"
      }${port.speed_mbps ? ` · ${fmtSpeed(port.speed_mbps)}` : ""}${
        vlan ? `\n${vlanSummary(vlan)}` : port.native_vlan ? ` · untagged VLAN ${port.native_vlan}` : ""
      }${vlan && vlan.drift.some((d) => d.severity === "drift") ? " · differs from Meridian" : ""}${port.neighbor ? `\nConnected to ${formatPortRefs(port.neighbor)}` : ""}`}
      className={cn(
        "relative flex h-9 w-9 shrink-0 flex-col items-center justify-center rounded border text-[10px] font-medium tabular-nums transition-colors",
        adminDown
          ? "border-dashed border-line bg-transparent text-ink-subtle"
          : up
            ? "border-healthy bg-healthy-soft text-healthy"
            : "border-line bg-transparent text-ink-muted",
        // Selection gets its OWN channel: a brand halo sitting off the chip's own border, so
        // it reads as "picked" rather than as another status. The offset matters -- a ring
        // flush against the border competes with the healthy/line border and, on a dense
        // 54-port faceplate, the two become one thick edge of ambiguous meaning.
        selected && "ring-2 ring-brand ring-offset-1 ring-offset-surface",
      )}
    >
      {/* In a multi-selection the halo alone is doing too much work -- it is one colour on a
          grid of 54, and colour is exactly the channel colour-blind operators lose. The tick
          states it in a second channel. Only in multi, because a single selection already has
          the detail panel open underneath saying which port it is. */}
      {selected && multi && (
        <span
          aria-hidden
          className="absolute -right-0.5 -top-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-brand text-[8px] font-bold leading-none text-white"
        >
          ✓
        </span>
      )}
      <span>{shortPortLabel(port.name)}</span>
      <span className="flex h-2 items-center gap-0.5">
        {port.poe_enabled && (
          <span aria-hidden title="Delivering PoE" className="text-[8px] leading-none">
            ⚡
          </span>
        )}
        {isUplink && (
          <span aria-hidden title="Uplink" className="text-[8px] leading-none">
            ↑
          </span>
        )}
        {/* Trunk marker: a letter, not a colour, so it survives colour-blindness and dark mode. */}
        {isTrunkLike(vlan) && (
          <span aria-hidden className="text-[8px] font-bold leading-none">
            T
          </span>
        )}
      </span>
    </button>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-subtle">
      <span className="inline-flex items-center gap-1">
        <span className="inline-block h-2.5 w-2.5 rounded-sm border border-healthy bg-healthy-soft" />
        up
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block h-2.5 w-2.5 rounded-sm border border-line" />
        down
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-block h-2.5 w-2.5 rounded-sm border border-dashed border-line" />
        disabled
      </span>
      <span>⚡ PoE</span>
      <span>↑ uplink</span>
      <span>
        <span className="font-bold">T</span> trunk
      </span>
    </div>
  );
}

/**
 * The hand-off from the faceplate to the port table.
 *
 * With 2+ ports picked it is the primary action of the sheet ("Edit 6 ports in table →"); with
 * zero or one it is a quiet link, so an operator who has never used the table still finds it.
 */
function TableHandoff({ href, selectedNumbers }: { href: string; selectedNumbers: string[] }) {
  const n = selectedNumbers.length;
  if (n < 2) {
    return (
      <Link to={href} className="self-start text-xs font-medium text-brand hover:underline">
        Edit ports in table →
      </Link>
    );
  }
  return (
    <div className="rounded-md border border-brand/40 bg-surface-2 p-3">
      <div className="text-sm font-medium text-ink">
        <span className="tabular-nums">{n}</span> ports selected
      </div>
      <p className="mt-0.5 break-words font-mono text-[11px] leading-relaxed text-ink-muted">
        {selectedNumbers.join(", ")}
      </p>
      <p className="mt-1 text-[11px] text-ink-subtle">
        Name them with a pattern such as <span className="font-mono">Room 10{"{port}"}</span>, or set
        VLAN, PoE, edge port and 802.1X in one edit — with the commands previewed first.
      </p>
      <Link
        to={href}
        className="mt-2 inline-flex min-h-touch items-center justify-center rounded bg-brand px-4 text-sm font-medium text-ink-invert hover:bg-brand-strong"
      >
        Edit {n} ports in table →
      </Link>
    </div>
  );
}

/**
 * The selected port: what it is, what is plugged into it, and the quick one-port edits (name,
 * access VLAN, edge port). Trunking, 802.1X, rate limits and voice VLAN live in the port table's
 * Advanced editor, which this links to rather than reimplements.
 *
 * STALE-PROPOSAL FIX (review finding, 2026-09-14). This panel used to set a `pending` proposal
 * when Preview was CLICKED, while field edits cleared only `preview`. A preview still in flight
 * then landed after the edit and re-showed the old proposal, and Apply sent it -- a change the
 * form no longer described. Now it follows the reactive-key pattern the other switch panels use
 * (S6a): the preview remembers the exact request KEY it was rendered from, the form derives its
 * current key on every render, and Apply is offered only while the two are equal. No field has to
 * remember to clear anything, and a late response can never revive a stale proposal.
 *
 * NAME is NOT part of the previewed change: "Save name" stores it immediately and it syncs to the
 * switch on its own (module header), so the preview below never carries a description.
 */
function PortDetail({
  serial,
  port,
  isUplink,
  vlanView,
  vlans,
  vlansLoading,
  vlansError,
  tableHref,
  onClose,
}: {
  serial: string;
  port: SwitchPortDetail;
  isUplink: boolean;
  /** This port's current VLANs (null while loading / unreadable). */
  vlanView: PortVlanView | null;
  /** The whole switch's VLAN view (sources, site VLANs). */
  vlans?: PortVlansResponse;
  vlansLoading: boolean;
  vlansError: boolean;
  /** Port table deep link with this switch + port preselected (Advanced settings live there). */
  tableHref: string;
  onClose: () => void;
}) {
  const currentName = port.alias ?? port.descr ?? "";
  const [name, setName] = useState(currentName);
  /**
   * The VLAN view the editor starts from. When switch/port-vlans could not be read, a minimal one
   * is built from the port list's untagged VLAN with tagged_known=false, so the edit still works
   * and its warnings say that hand-set tagged VLANs are not known.
   */
  const view: PortVlanView = vlanView ?? {
    port: port.name, label: formatSwitchPort(port.name), mode: "access", mode_reason: "",
    native: { vlan: port.native_vlan ?? null, name: null, source: "live" },
    tagged: [], tagged_source: "none", tagged_known: false, seen_vlans: [], intent: null,
    applied_at: null,
    stp: { edge: null, role: port.stp_role ?? null, state: port.stp_state ?? null, edge_configured: null },
    needs: [], drift: [],
  };
  /** null = not editing VLANs. "Edit VLANs" copies the CURRENT VLANs in, so nothing is blind. */
  const [vlanEdit, setVlanEdit] = useState<VlanEdit | null>(null);
  /**
   * STP edge port, pre-filled with the switch's live oper-edge state (lib/portVlans edgeInitial).
   * `edgeTouched` keeps a late-arriving view from overwriting the operator's choice; only a value
   * that differs from the current one becomes part of the change. UNVERIFIED on hardware -- see
   * services/switch_portconfig _EDGE_PORT_ON.
   */
  const edgeNow = edgeInitial(vlanView);
  const [edgeChoice, setEdgeChoice] = useState<"" | "on" | "off">("");
  const [edgeTouched, setEdgeTouched] = useState(false);
  const edgePort = edgeTouched ? edgeChoice : edgeNow;
  /** "Save to startup-config" -- default on (the old hard-coded save:true), now a choice. */
  const [save, setSave] = useState(true);
  /** The last dry-run response, and the request key it was rendered from. */
  const [preview, setPreview] = useState<PortConfigResponse | null>(null);
  const [previewed, setPreviewed] = useState<{ change: PortChange; save: boolean; key: string } | null>(null);
  // The result plus whether a save was requested -- needed to word it honestly (saved:null).
  const [applied, setApplied] = useState<{ res: PortConfigResponse; save: boolean } | null>(null);

  const apply = usePortConfigApply(serial);
  const poe = usePortPoeAction(serial);
  /** Immediate name save (option C): no preview, the switch gets it in the background. */
  const saveName = useSetPortLabel(serial);

  const nameDirty = sanitizePortName(name) !== sanitizePortName(currentName);
  /** Save the name now. Not part of the previewed CLI change below. */
  const doSaveName = () => {
    if (!nameDirty || saveName.isPending) return;
    saveName.mutate({ port: port.name, label: sanitizePortName(name) });
  };
  // VLAN delta against the CURRENT VLANs (lib/portVlans): only what the operator changed.
  const vlanErrors = vlanEdit ? vlanDelta(view, vlanEdit).errors : [];
  const vlanValid = vlanErrors.length === 0;

  /** The change the form describes RIGHT NOW -- only what the operator actually changed. */
  const change: PortChange = { port: port.name };
  const vlanChange = vlanEdit && vlanValid ? buildVlanChange(view, vlanEdit) : null;
  if (vlanChange) Object.assign(change, vlanChange);
  if (edgeTouched && edgePort && edgePort !== edgeNow) change.edge_port = edgePort === "on";
  const dirty = Object.keys(change).length > 1;
  const currentKey = JSON.stringify({ change, save });
  // Offer Apply only while the form still renders exactly what was previewed.
  const fresh = !!preview && preview.dry_run && previewed?.key === currentKey;

  /** Step 1: render the commands (dry_run -- no device contact) and remember what they came from. */
  const doPreview = () => {
    if (!dirty || !vlanValid) return;
    const snapshot = { change, save, key: currentKey };
    setApplied(null);
    apply.mutate(
      { changes: [snapshot.change], dry_run: true, save: snapshot.save },
      {
        onSuccess: (res) => {
          setPreview(res);
          setPreviewed(snapshot);
        },
      },
    );
  };

  /** Step 2: run EXACTLY the previewed change -- never a rebuild of the form. */
  const doApply = () => {
    if (!fresh || !previewed) return;
    const snap = previewed;
    apply.mutate(
      { changes: [snap.change], dry_run: false, save: snap.save },
      {
        onSuccess: (res) => {
          setApplied({ res, save: snap.save });
          setPreview(null);
          setPreviewed(null);
        },
      },
    );
  };

  return (
    <div className="rounded-md border border-line bg-surface-2 p-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-sm font-medium text-ink" title={port.name}>
            Port {formatSwitchPort(port.name)}
          </div>
          <div className="text-xs text-ink-muted">
            {port.admin_up === false
              ? "Administratively disabled"
              : port.status === "up"
                ? `Up · ${fmtSpeed(port.speed_mbps)}`
                : "Down"}
            {vlanView
              ? ` · ${vlanSummary(vlanView)}`
              : port.native_vlan != null && ` · untagged VLAN ${port.native_vlan}`}
            {/* Real draw, not a capability flag. "PoE 6.1W" tells an operator a device is
                actually being powered; "PoE capable" (all SNMP could offer) does not. */}
            {port.poe_power_mw ? ` · PoE ${(port.poe_power_mw / 1000).toFixed(1)}W` : ""}
            {!vlanView && port.stp_state && port.stp_state !== "Forwarding" && ` · STP ${port.stp_state}`}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-touch w-touch shrink-0 items-center justify-center rounded text-xs text-ink-muted hover:bg-surface hover:text-ink"
          aria-label="Close port detail"
        >
          ✕
        </button>
      </div>

      {/* What is on the other end. This is the line that turns a port list into a topology
          map, and it is why the LLDP neighbour is shown here rather than behind a click. */}
      <dl className="mb-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
        {/* The name and whether the switch holds it yet -- for every role, read-only included. */}
        <dt className="text-ink-muted">Name</dt>
        <dd className="flex min-w-0 flex-col gap-1">
          <span className="break-words text-ink">{currentName || <span className="text-ink-subtle">— not named</span>}</span>
          <NameSyncBadge serial={serial} sync={port.name_sync} />
        </dd>
        <dt className="text-ink-muted">Connected to</dt>
        <dd className="min-w-0 break-words font-mono text-ink">
          {port.neighbor ? formatPortRefs(port.neighbor) : <span className="text-ink-subtle">— nothing reported</span>}
        </dd>
        {(port.connected_macs?.length ?? 0) > 0 && (
          <>
            <dt className="text-ink-muted">MACs</dt>
            <dd className="min-w-0 font-mono text-ink">
              {port.connected_macs!.slice(0, 3).join(", ")}
              {port.connected_macs!.length > 3 && ` +${port.connected_macs!.length - 3}`}
            </dd>
          </>
        )}
      </dl>

      {/* Current VLANs + spanning tree, for every role (read-only). */}
      <div className="mb-3">
        <PortVlanFacts view={vlanView} sources={vlans?.sources} loading={vlansLoading} error={vlansError} />
      </div>

      {/* Writes: msp_admin only -- the backend gates ports/apply on is_admin, so lower roles would
          only meet a 403. They keep the read-only facts above. */}
      <RoleGate min="msp_admin" mode="hide">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1 text-xs text-ink-muted">
            <label htmlFor={`port-name-${port.name}`}>Port name</label>
            <div className="flex gap-2">
              <input
                id={`port-name-${port.name}`}
                className="min-h-touch min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink"
                placeholder={`Port ${formatSwitchPort(port.name)}`}
                value={name}
                maxLength={128}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") doSaveName();
                }}
              />
              <Button variant="secondary" disabled={!nameDirty || saveName.isPending} onClick={doSaveName}>
                {saveName.isPending ? "Saving…" : "Save name"}
              </Button>
            </div>
            <span className="text-[11px] leading-snug text-ink-subtle">
              Names sync to the switch automatically — no preview needed.
            </span>
            {saveName.error && (
              <span className="text-[11px] text-critical">
                The name was not saved: {saveName.error instanceof Error ? saveName.error.message : "request failed"}
              </span>
            )}
          </div>

          {/* VLANs: edited FROM the current ones; the change line states the delta. */}
          {vlanEdit ? (
            <>
              <PortVlanEditor view={view} edit={vlanEdit} setEdit={setVlanEdit} siteVlans={vlans?.site_vlans ?? []} />
              <Button variant="ghost" onClick={() => setVlanEdit(null)}>
                Discard VLAN changes
              </Button>
            </>
          ) : (
            <Button variant="secondary" disabled={vlansLoading} onClick={() => setVlanEdit(editFromView(view))}>
              {vlansLoading ? "Reading VLANs…" : "Edit VLANs"}
            </Button>
          )}

          {/* STP edge port. The warning sits on the control: edge on a switch-to-switch link can
              loop the network. Command UNVERIFIED on hardware (services/switch_portconfig). */}
          <label className="flex flex-col gap-1 text-xs text-ink-muted">
            Edge port (spanning-tree edge-port)
            <select
              className="min-h-touch rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink"
              value={edgePort}
              onChange={(e) => {
                setEdgeTouched(true);
                setEdgeChoice(e.target.value as typeof edgePort);
              }}
            >
              {/* "(unchanged)" only while the current state is unknown; otherwise the current
                  value is pre-selected and labelled as such. */}
              {edgeNow === "" && <option value="">(unchanged — current state unknown)</option>}
              <option value="on">On (edge port){edgeNow === "on" ? " — current" : ""}</option>
              <option value="off">Off{edgeNow === "off" ? " — current" : ""}</option>
            </select>
            <span className="text-[11px] leading-snug text-ink-subtle">
              For ports connected to a gateway, AP or end device: the port forwards right away
              after a link change instead of waiting ~30 s. Never set it on a port that connects
              to another switch.
            </span>
          </label>

          {/* Persist or not. Part of the request key, so changing it invalidates the preview. */}
          <SaveToStartupCheckbox checked={save} onChange={setSave} />

          {/* --- propose --- */}
          {!fresh && (
            <Button variant="secondary" disabled={!dirty || !vlanValid || apply.isPending} onClick={doPreview}>
              {apply.isPending ? "Rendering…" : "Preview changes"}
            </Button>
          )}
          {preview && !fresh && (
            <p className="text-[11px] text-ink-subtle">Changed since the preview — preview again before applying.</p>
          )}

          {/* 400s (bad VLAN, ...) and transport errors. A refused apply is a result, below. */}
          {apply.error && <ErrorBanner error={apply.error} />}

          {/* --- the commands, then confirm (only while they still match the form) --- */}
          {fresh && preview && (
            <div className="rounded border border-line bg-surface p-2">
              <div className="mb-1 text-xs font-medium text-ink">
                These commands will run on the switch
              </div>
              {/* Direct, or via which gateway -- the path Apply will use. */}
              <AccessPathLine access={preview.access} />
              {/* What changes, in VLANs, judged by the controller against the current VLANs. */}
              {!preview.error && <VlanDiffList diffs={preview.vlan_diff} />}
              {preview.error ? (
                <p className="text-xs text-critical">{preview.error}</p>
              ) : (
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-ink-muted">
                  {preview.commands.join("\n")}
                </pre>
              )}
              <div className="mt-2 flex gap-2">
                <Button
                  variant="primary"
                  disabled={apply.isPending || !!preview.error}
                  onClick={doApply}
                >
                  {apply.isPending ? "Applying…" : "Apply to switch"}
                </Button>
                <Button variant="ghost" onClick={() => setPreview(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Honest outcome: "saved" only when the switch confirmed the flash write. Anything
              short of that says so and offers the save (lib/switchApplyResult). */}
          {applied && (
            <OutcomeNote message={describeApplyOutcome(applied.res, { saveRequested: applied.save })}>
              {needsSave(applied.res) && <SaveRunningConfig serial={serial} compact />}
            </OutcomeNote>
          )}

          <PoeControls serial={serial} port={port} isUplink={isUplink} poe={poe} />
        </div>
      </RoleGate>

      <Link to={tableHref} className="mt-3 inline-block text-xs font-medium text-brand hover:underline">
        802.1X, rate limits, voice VLAN →
      </Link>
    </div>
  );
}

/**
 * PoE actions, with the uplink guard.
 *
 * Cycling PoE on the port that carries the switch's own uplink -- or on a port feeding the AP
 * you are currently connected through -- drops the thing you are working over. That warning
 * belongs ON the control at the moment of use, not in a tooltip someone will never open, so
 * on the uplink the action is blocked outright rather than merely cautioned: there is no
 * version of "cycle the uplink from the web UI" that ends well, and an override here would
 * only make the mistake reachable.
 */
function PoeControls({
  serial,
  port,
  isUplink,
  poe,
}: {
  serial: string;
  port: SwitchPortDetail;
  isUplink: boolean;
  poe: ReturnType<typeof usePortPoeAction>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<PortConfigResponse | null>(null);
  void serial;

  if (!port.poe_enabled) return null;

  if (isUplink) {
    return (
      <div className="rounded border border-warn bg-warn-soft p-2 text-xs text-warn">
        <span className="font-medium">PoE actions are disabled on the uplink.</span> Cycling
        power here would drop this switch's own connection to the controller.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {!confirming ? (
        <Button variant="secondary" onClick={() => setConfirming(true)}>
          Cycle PoE on this port
        </Button>
      ) : (
        <div className="rounded border border-warn bg-warn-soft p-2">
          <p className="text-xs text-warn">
            <span className="font-medium">This reboots whatever is powered by this port.</span>{" "}
            {port.neighbor
              ? `${port.neighbor} will drop and take up to a few minutes to come back.`
              : "Any device on this port will drop and take up to a few minutes to come back."}
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              variant="primary"
              disabled={poe.isPending}
              onClick={() =>
                poe.mutate(
                  { port: port.name, action: "cycle", dry_run: false },
                  {
                    onSuccess: (res) => {
                      setResult(res);
                      setConfirming(false);
                    },
                  },
                )
              }
            >
              {poe.isPending ? "Cycling…" : "Cycle power"}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {result && (
        <div className={cn("text-xs", result.ok ? "text-healthy" : "text-critical")}>
          {result.ok ? "Power cycled." : result.error || "The switch rejected the action."}
        </div>
      )}
    </div>
  );
}

/** "Ethernet1/0/12" -> "12". The faceplate has room for the number, not the interface name. */
function shortPortLabel(name: string): string {
  // uCentral names are ZERO-based; the faceplate and the CLI count from one (lib/switchPorts).
  if (isUcentralPortName(name)) return formatSwitchPort(name);
  // `?? ` rather than `m ? m[1] : ...`: under noUncheckedIndexedAccess a capture group is
  // typed string | undefined even inside a truthiness check on the match.
  return name.match(/(\d+)\s*$/)?.[1] ?? name.slice(-3);
}

function fmtSpeed(mbps?: number | null): string {
  if (!mbps) return "—";
  return mbps >= 1000 ? `${mbps / 1000}G` : `${mbps}M`;
}
